import React from 'react';
import { Heart, MessageCircle, Bookmark, Share, ThumbsUp, Send, Play, MoreHorizontal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const thumbUrl = (clip) => (clip?.thumbnail_path ? `${API}/clips/${clip.id}/thumbnail` : null);

// ---- TikTok ----
const TikTokPreview = ({ clip, data }) => (
  <div className="mx-auto max-w-[260px] rounded-2xl overflow-hidden border border-white/10 bg-black relative aspect-[9/16]" data-testid="preview-tiktok">
    {thumbUrl(clip) ? <img src={thumbUrl(clip)} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${clip?.thumbnail_color || '#FF3D7F'}, #050505)` }} />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
    <div className="absolute top-3 left-0 right-0 flex justify-center gap-4 text-xs font-semibold text-white/80"><span>Following</span><span className="text-white border-b-2 border-white pb-1">For You</span></div>
    <div className="absolute bottom-3 left-3 right-14">
      <div className="text-white text-sm font-bold mb-1">@{data.user?.name?.toLowerCase()?.replace(/\s+/g, '') || 'you'}</div>
      <div className="text-white text-xs leading-snug line-clamp-3 whitespace-pre-line">{data.caption}</div>
      {data.hashtags?.length ? <div className="text-white/90 text-xs mt-1">{data.hashtags.slice(0, 6).map(h => `#${h}`).join(' ')}</div> : null}
      {data.cta ? <div className="text-white/80 text-[11px] italic mt-1">{data.cta}</div> : null}
    </div>
    <div className="absolute bottom-3 right-2 flex flex-col gap-3 items-center text-white/90 text-[10px]">
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Heart className="w-4 h-4" /></div><span>142K</span>
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></div><span>3.2K</span>
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Bookmark className="w-4 h-4" /></div>
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Share className="w-4 h-4" /></div>
    </div>
  </div>
);

// ---- Instagram Reels ----
const InstagramPreview = ({ clip, data }) => (
  <div className="mx-auto max-w-[260px] rounded-2xl overflow-hidden border border-white/10 bg-black relative aspect-[9/16]" data-testid="preview-instagram">
    {thumbUrl(clip) ? <img src={thumbUrl(clip)} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${clip?.thumbnail_color || '#E1306C'}, #050505)` }} />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/40" />
    <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white">
      <span className="font-semibold text-sm">Reels</span>
      <div className="flex items-center gap-1 text-[10px] font-mono border border-white/40 rounded px-1.5 py-0.5"><Play className="w-2.5 h-2.5" />1.2M</div>
    </div>
    <div className="absolute bottom-3 left-3 right-12">
      <div className="text-white text-xs font-bold mb-1">@{data.user?.name?.toLowerCase()?.replace(/\s+/g, '') || 'you'}</div>
      <div className="text-white text-xs leading-snug line-clamp-3 whitespace-pre-line">{data.caption}</div>
      {data.hashtags?.length ? <div className="text-white/80 text-[11px] mt-1">{data.hashtags.slice(0, 5).map(h => `#${h}`).join(' ')}</div> : null}
    </div>
    <div className="absolute bottom-3 right-2 flex flex-col gap-3 items-center text-white/90 text-[10px]">
      <Heart className="w-6 h-6" /><MessageCircle className="w-6 h-6" /><Send className="w-6 h-6" /><Bookmark className="w-6 h-6" />
    </div>
  </div>
);

// ---- YouTube Shorts ----
const YouTubePreview = ({ clip, data }) => (
  <div className="mx-auto max-w-[260px] rounded-2xl overflow-hidden border border-white/10 bg-black relative aspect-[9/16]" data-testid="preview-youtube">
    {thumbUrl(clip) ? <img src={thumbUrl(clip)} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${clip?.thumbnail_color || '#FF0000'}, #050505)` }} />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/60" />
    <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white">
      <span className="font-semibold text-sm bg-black/40 rounded px-2 py-0.5">Shorts</span>
      <MoreHorizontal className="w-4 h-4" />
    </div>
    <div className="absolute bottom-3 left-3 right-14">
      {data.title ? <div className="text-white font-bold text-sm mb-1.5 leading-tight line-clamp-2">{data.title}</div> : null}
      <div className="text-white text-xs font-semibold mb-1">@{data.user?.name?.toLowerCase()?.replace(/\s+/g, '') || 'yourchannel'}</div>
      <div className="text-white/90 text-[11px] leading-snug line-clamp-2 whitespace-pre-line">{data.caption}</div>
    </div>
    <div className="absolute bottom-3 right-2 flex flex-col gap-3 items-center text-white/90 text-[10px]">
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><ThumbsUp className="w-4 h-4" /></div><span>42K</span>
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></div><span>892</span>
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Share className="w-4 h-4" /></div>
    </div>
  </div>
);

// ---- LinkedIn ----
const LinkedInPreview = ({ clip, data }) => (
  <div className="mx-auto max-w-[380px] rounded-lg overflow-hidden border border-white/10 bg-white text-zinc-900" data-testid="preview-linkedin">
    <div className="p-3 flex items-start gap-2.5">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A66C2] to-[#004182] flex items-center justify-center text-white font-bold text-sm">
        {(data.user?.name || 'U').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm leading-tight">{data.user?.name || 'You'}</div>
        <div className="text-xs text-zinc-500">Content Creator · <span className="text-zinc-400">1st</span></div>
        <div className="text-xs text-zinc-500">Just now · 🌍</div>
      </div>
      <MoreHorizontal className="w-5 h-5 text-zinc-400" />
    </div>
    <div className="px-3 pb-3 text-sm whitespace-pre-line line-clamp-6">{data.caption}
      {data.hashtags?.length ? <div className="text-[#0A66C2] mt-2 text-sm">{data.hashtags.slice(0, 6).map(h => `#${h}`).join(' ')}</div> : null}
      {data.cta ? <div className="mt-2 italic text-zinc-600">{data.cta}</div> : null}
    </div>
    <div className="relative aspect-video bg-black">
      {thumbUrl(clip) ? <img src={thumbUrl(clip)} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${clip?.thumbnail_color || '#0A66C2'}, #001a33)` }} />}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center"><Play className="w-6 h-6 text-white ml-1" /></div>
      </div>
    </div>
    <div className="border-t border-zinc-200 px-3 py-1.5 flex justify-around text-[13px] text-zinc-600 font-semibold">
      <span className="flex items-center gap-1.5 py-1.5"><ThumbsUp className="w-4 h-4" /> Like</span>
      <span className="flex items-center gap-1.5 py-1.5"><MessageCircle className="w-4 h-4" /> Comment</span>
      <span className="flex items-center gap-1.5 py-1.5"><Share className="w-4 h-4" /> Repost</span>
      <span className="flex items-center gap-1.5 py-1.5"><Send className="w-4 h-4" /> Send</span>
    </div>
  </div>
);

const PlatformPreview = ({ provider, clip, data }) => {
  switch (provider) {
    case 'tiktok':    return <TikTokPreview clip={clip} data={data} />;
    case 'instagram': return <InstagramPreview clip={clip} data={data} />;
    case 'youtube':   return <YouTubePreview clip={clip} data={data} />;
    case 'linkedin':  return <LinkedInPreview clip={clip} data={data} />;
    default: return null;
  }
};

export default PlatformPreview;
